import { ConfirmDialogComponent } from "@/components/confirm-dialog/confirm-dialog.component";
import { FileUploadDialogComponent } from "@/components/file-upload-dialog/file-upload-dialog.component";
import { IframeDialogComponent } from "@/components/iframe-dialog/iframe-dialog.component";
import { InfoDialogComponent } from "@/components/info-dialog/info-dialog.component";
import { MaintenanceDialogComponent } from "@/components/maintenance-dialog/maintenance-dialog.component";
import { PdfViewerDialogComponent } from "@/components/pdf-viewer-dialog/pdf-viewer-dialog.component";
import { PropertyAddFullDialogComponent } from "@/components/property-add-full-dialog/property-add-full-dialog.component";
import { PropertyAddSimpleDialogComponent } from "@/components/property-add-simple-dialog/property-add-simple-dialog.component";
import { PropertyCertifyDialogComponent } from "@/components/property-certify-dialog/property-certify-dialog.component";
import { PropertyConsumptionAddDialogComponent } from "@/components/property-consumption-add-dialog/property-consumption-add-dialog.component";
import {
  PropertyCustomServicesDialogComponent,
  PropertyCustomServicesDialogData,
} from "@/components/property-custom-services-dialog/property-custom-services-dialog.component";
import {
  PropertyDataSyncDialogComponent,
  PropertyDataSyncDialogData,
} from "@/components/property-data-sync-dialog/property-data-sync-dialog.component";
import {
  PropertyDpeUpdateDialogComponent,
  PropertyDpeUpdateDialogData,
} from "@/components/property-dpe-update-dialog/property-dpe-update-dialog.component";
import { PropertyEquipmentDialogComponent } from "@/components/property-equipment-dialog/property-equipment-dialog.component";
import { PropertyNotesUpdateDialogComponent } from "@/components/property-notes-update-dialog/property-notes-update-dialog.component";
import {
  PropertyServicesDialogComponent,
  PropertyServicesDialogData,
} from "@/components/property-services-dialog/property-services-dialog.component";
import { PropertyTransferInfoDialogComponent } from "@/components/property-transfer-info-dialog/property-transfer-info-dialog.component";
import { PropertyTransfertDialogComponent } from "@/components/property-transfert-dialog/property-transfert-dialog.component";
import { PropertyUpdateDialogComponent } from "@/components/property-update-dialog/property-update-dialog.component";
import { PropertyWorkDialogComponent } from "@/components/property-work-dialog/property-work-dialog.component";
import { QRCodeDialogComponent } from "@/components/qrcode-dialog/qrcode-dialog.component";
import {
  SaleUpdateDialogComponent,
  SaleUpdateDialogData,
} from "@/components/sale-update-dialog/sale-update-dialog.component";
import { ShareDialogComponent } from "@/components/share-dialog/share-dialog.component";
import { TimelineEventDialogComponent } from "@/components/timeline-event-dialog/timeline-event-dialog.component";
import { VisitFeedbackDialogComponent } from "@/components/visit-feedback-dialog/visit-feedback-dialog.component";
import { ActionDoneType } from "@/enums/ActionDoneType.enum";
import { ObjectUpdatedType } from "@/enums/ObjectUpdatedType.enum";
import { AppConstants } from "@/helpers/app.constants";
import { isDropBoxDocument } from "@/helpers/file.helper";
import { PropertyHelper } from "@/helpers/property.helper";
import { SharingHelper } from "@/helpers/sharing.helper";
import { UserHelper } from "@/helpers/user.helper";
import { Clipboard } from "@angular/cdk/clipboard";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import {
  CertificationRequest,
  CertificationType,
  CertificationsService,
  Document,
  DropBoxDocument,
  PropertiesService,
  RealProperty,
  SharingDataCategory,
  SharingRequest,
  SharingService,
  VaultService,
} from "@eemo/api-client";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { AlertService } from "./alert.service";
import { AppConfigService } from "./app-config.service";
import { FileUploadType } from "./file-upload.service";

@Injectable({ providedIn: "root" })
export class ActionService {
  prop: RealProperty;

  _showPhotosTriggered = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private userHelper: UserHelper,
    private propHelper: PropertyHelper,
    private alertService: AlertService,
    private appConfigService: AppConfigService,
    private certificationService: CertificationsService,
    private sharingHelper: SharingHelper,
    private propertiesService: PropertiesService,
    private vaultService: VaultService,
    private sharingService: SharingService,
    private clipboard: Clipboard
  ) {
    this.appConfigService.selectedPropObs.subscribe((p) => (this.prop = p));
  }

  get showPhotosTriggered$(): Observable<boolean> {
    return this._showPhotosTriggered.asObservable();
  }

  reloadPage(): void {
    location.reload();
  }

  goToHome(): void {
    this.goToLink("/home");
  }

  goToAdmin(): void {
    this.goToLink("/admin");
  }

  goToProfile(): void {
    this.goToLink("/profile");
  }

  goToKpi(): void {
    this.goToLink("/kpi");
  }

  goToHelp(): void {
    this.goToLink("/help");
  }

  goToLink(link: string): void {
    this.router.navigate([link]);
  }

  openHelp(): void {
    const Beacon = window["Beacon"];
    if (Beacon) {
      Beacon("open");
    }
  }

  copyPropVCardUrl(prop: RealProperty) {
    this.clipboard.copy(
      location.protocol +
        "//" +
        location.host +
        this.propHelper.getVCardUrl(prop)
    );
    // alert must be triggered from a distinct thread
    setTimeout(() => this.alertService.success("Lien copié"), 1);
  }

  copyPropNfcUrl(prop: RealProperty) {
    this.clipboard.copy(
      location.protocol + "//" + location.host + this.propHelper.getNfcUrl(prop)
    );
    setTimeout(() => this.alertService.success("Lien copié"), 1);
  }

  addProperty(): void {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      if (this.userHelper.canAccessPropertyCreateFull()) {
        const dialogRef = this.dialog.open(PropertyAddFullDialogComponent, {
          data: {},
        });
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.goToProperty(result);
          }
        });
      } else {
        const dialogRef = this.dialog.open(PropertyAddSimpleDialogComponent, {
          data: {
            withAddressSearch: true,
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.goToProperty(result);
          }
        });
      }
    }
  }

  togglePropertyArchive(prop: RealProperty): void {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      let message = "le désarchivage";
      let details;
      if (!prop.archived) {
        message = "l'archivage";
        details = "Les partages seront désactivés...";
      }
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          message: `Confirmez-vous ${message} du bien ${prop.name} ?`,
          details,
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doTogglePropertyArchive(prop);
        }
      });
    }
  }

  doTogglePropertyArchive(prop: RealProperty): void {
    const message = prop.archived ? "Bien désarchivé" : "Bien archivé";
    this.propertiesService
      .toggleRealPropertyArchived(prop.uid)
      .subscribe(() => {
        this.alertService.success(message);
        this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.PROPERTY);
      });
  }

  goToProperty(propId): void {
    this.router.navigate(["/property/" + propId]);
  }

  editName() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyUpdateDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(
            ObjectUpdatedType.PROPERTY
          );
        }
      });
    }
  }

  editNotes() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyNotesUpdateDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.NOTES);
        }
      });
    }
  }

  uploadDoc(isScan = false) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const fileUploadType = isScan
        ? FileUploadType.SCAN_DOC
        : FileUploadType.DOC;
      const dialogRef = this.dialog.open(FileUploadDialogComponent, {
        // width: '20rem'
        data: {
          uploadType: fileUploadType,
          props: [this.prop],
          defaultCategory: this.appConfigService.currentDocCategory,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.DOC);
        }
      });
    }
  }

  uploadPhoto() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(FileUploadDialogComponent, {
        // width: '20rem'
        data: { uploadType: FileUploadType.PHOTO, props: [this.prop] },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.PHOTO);
        }
      });
    }
  }

  showPhotos() {
    this._showPhotosTriggered.next(true);
    this._showPhotosTriggered.next(false);
  }

  addShare() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ShareDialogComponent, {
        // width: '20rem'
        data: { props: [this.prop] },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerActionDone(ActionDoneType.SHARE);
        }
      });
    }
  }

  addShareFromRequest(request: SharingRequest) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ShareDialogComponent, {
        // width: '20rem'
        data: {
          props: [request.property],
          targetUserEmail: request.user.email,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerActionDone(ActionDoneType.SHARE);
        }
      });
    }
  }

  confirmCertificationRequest() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            'Confirmez-vous la demande de certification du bien "' +
            this.prop.name +
            '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doCertificationRequest();
        }
      });
    }
  }

  doCertificationRequest() {
    this.certificationService
      .requestCertification(this.prop.uid, { type: CertificationType.OWNER })
      .subscribe(() => {
        this.alertService.success(
          "Demande de certification effectuée avec succès"
        );
        this.appConfigService.triggerActionDone(ActionDoneType.CERTIFY_REQUEST);
      });
  }

  confirmDeleteCertificationRequest(request: CertificationRequest) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            "Confirmez-vous l'annulation de la demande de certification ?",
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteCertificationRequest(request);
        }
      });
    }
  }

  doDeleteCertificationRequest(request: CertificationRequest) {
    this.certificationService
      .deleteCertificationRequest(request.uid)
      .subscribe(() => {
        this.alertService.success("Demande de certification annulée");
        this.appConfigService.triggerActionDone(ActionDoneType.CERTIFY_REQUEST);
      });
  }

  transfertOwnership() {
    if (this.userHelper.isAdmin()) {
      // transfert is allowed only for admins
      // prevents to open multiple dialog
      if (this.dialog.openDialogs.length === 0) {
        const dialogRef = this.dialog.open(PropertyTransfertDialogComponent, {
          // width: '20rem'
          data: { prop: this.prop },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.appConfigService.triggerActionDone(ActionDoneType.TRANSFER);
            this.router.navigate(["home"]);
          }
        });
      }
    } else {
      // show information poppup
      // prevents to open multiple dialog
      if (this.dialog.openDialogs.length === 0) {
        this.dialog.open(PropertyTransferInfoDialogComponent, {
          // width: '20rem'
          data: { prop: this.prop },
        });
      }
    }
  }

  certify() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyCertifyDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerActionDone(ActionDoneType.CERTIFY);
          this.router.navigate(["home"]);
        }
      });
    }
  }

  confirmDeleteProp() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            'Confirmez-vous la suppression du bien "' + this.prop.name + '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteProp();
        }
      });
    }
  }

  doDeleteProp() {
    this.propertiesService.deleteRealProperty(this.prop.uid).subscribe(() => {
      this.alertService.success("Suppression effectuée avec succès");
      this.router.navigate(["/home"]);
    });
  }

  showQrCode() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(QRCodeDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop },
      });
    }
  }

  addConsumptions() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(
        PropertyConsumptionAddDialogComponent,
        {
          // width: '20rem'
          data: { prop: this.prop },
        }
      );

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(
            ObjectUpdatedType.COMSUMPTION
          );
        }
      });
    }
  }

  addMaintenance() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(MaintenanceDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(
            ObjectUpdatedType.MAINTENANCE
          );
        }
      });
    }
  }

  addEquipment() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyEquipmentDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(
            ObjectUpdatedType.EQUIPMENT
          );
        }
      });
    }
  }

  addEvent() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(TimelineEventDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.EVENT);
        }
      });
    }
  }

  addWork() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyWorkDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.WORK);
        }
      });
    }
  }

  addPropertyService() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: PropertyServicesDialogData = { property: this.prop };
      const dialogRef = this.dialog.open(PropertyServicesDialogComponent, {
        data,
      });

      dialogRef.afterClosed().subscribe(() => {
        this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.SERVICE);
      });
    }
  }

  addCustomPropertyService() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: PropertyCustomServicesDialogData = {
        property: this.prop,
        edition: false,
      };
      const dialogRef = this.dialog.open(
        PropertyCustomServicesDialogComponent,
        {
          data,
        }
      );

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(
            ObjectUpdatedType.CUSTOM_CONTACT
          );
        }
      });
    }
  }

  addPropertySale() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: SaleUpdateDialogData = { prop: this.prop };
      const dialogRef = this.dialog.open(SaleUpdateDialogComponent, {
        data,
      });

      dialogRef.afterClosed().subscribe(() => {
        this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.SALE);
      });
    }
  }

  addVisitFeedback(shareUid: string) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(VisitFeedbackDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop, sharingUid: shareUid },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerActionDone(ActionDoneType.SHARE);
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.SHARE);
        }
      });
    }
  }

  viewVisitFeedback(shareUid: string) {
    this.sharingService.getSharingVisitFeedback(shareUid).subscribe((f) => {
      // prevents to open multiple dialog
      if (this.dialog.openDialogs.length === 0) {
        this.dialog.open(VisitFeedbackDialogComponent, {
          // width: '20rem'
          data: { prop: this.prop, sharingUid: shareUid, visitFeedback: f },
        });
      }
    });
  }

  showPropertyDataSync() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: PropertyDataSyncDialogData = { prop: this.prop };
      this.dialog.open(PropertyDataSyncDialogComponent, {
        data,
      });
    }
  }

  showPropertyDpeNumberUpdate() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: PropertyDpeUpdateDialogData = { prop: this.prop };
      this.dialog.open(PropertyDpeUpdateDialogComponent, {
        data,
      });
    }
  }

  showViewDefaultRightInfo() {
    // prevents to open multiple dialog
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Mode consultation par défaut",
        content:
          "<p>Par défaut, le mode consultation autorise l'accès à toutes les données excepté les catégories <i>" +
          this.sharingHelper.getSharingDataCategoryName(
            SharingDataCategory.SHARING
          ) +
          "</i> et <i>" +
          this.sharingHelper.getSharingDataCategoryName(
            SharingDataCategory.PEOPLE
          ) +
          "</i>.</p><p>&nbsp;</p>" +
          "<p> Si vous souhaitez donner accès à ces catégories, vous devez les autoriser lors de la création du partage en les cochant dans la partie <i>Sélection</i>.</p>" +
          "<p>&nbsp;</p><p>A noter que si le destinataire est un notaire, il a accès par défaut à la catégorie <i>" +
          this.sharingHelper.getSharingDataCategoryName(
            SharingDataCategory.PEOPLE
          ) +
          "</i>.",
      },
    });
  }

  showViewSelectedRightInfo(selected: string) {
    // prevents to open multiple dialog
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Données accessibles",
        content:
          "<p>Les données accessibles via ce partage sont: " +
          selected +
          "</p>",
      },
    });
  }

  viewDoc(
    doc: Document | DropBoxDocument,
    validateMode = false
  ): Observable<boolean> {
    //const viewableInNewPage = this.propHelper.isDocumentViewableInNewPage(doc);
    const viewableInIFrame = this.propHelper.isDocumentViewableInIFrame(doc);
    const viewableInGoogleDoc =
      this.propHelper.isDocumentViewableInGoogleDoc(doc);
    const viewableInPdfViewer =
      this.propHelper.isDocumentViewableInPdfViewer(doc);
    if (viewableInPdfViewer) {
      return this._viewDocInPdfViewer(doc, validateMode);
    } else if (viewableInGoogleDoc) {
      return this._viewDocInGoogleDoc(doc);
    } else if (viewableInIFrame) {
      return this._viewDocInIFrame(doc, validateMode);
    }
  }

  private _viewDocInIFrame(
    doc: Document | DropBoxDocument,
    validateMode: boolean
  ): Observable<boolean> {
    const read = new Subject<boolean>();
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(IframeDialogComponent, {
        data: {
          title: doc.name,
          url: isDropBoxDocument(doc)
            ? this.propHelper.getDropBoxDocumentUrl(doc)
            : this.propHelper.getDocumentUrl(doc),
          isImage: this.propHelper.isDocumentImage(doc),
          validateMode,
        },
      });
      dialogRef.afterClosed().subscribe((result) => read.next(result));
    }
    return read.asObservable();
  }

  private _viewDocInGoogleDoc(
    doc: Document | DropBoxDocument
  ): Observable<boolean> {
    const read = new Subject<boolean>();
    this.vaultService.createDocumentAnonymousToken(doc.uid).subscribe({
      next: (token) => {
        // prevents to open multiple dialog
        if (this.dialog.openDialogs.length === 0) {
          const dialogRef = this.dialog.open(IframeDialogComponent, {
            data: {
              title: doc.name,
              doRetry: true,
              url: isDropBoxDocument(doc)
                ? this.propHelper.getGoogleDropBoxDocumentPreviewUrl(
                    doc,
                    token.token
                  )
                : this.propHelper.getGoogleDocumentPreviewUrl(doc, token.token),
            },
          });
          dialogRef.afterClosed().subscribe((result) => read.next(result));
        }
      },
    });
    return read.asObservable();
  }

  private _viewDocInPdfViewer(
    doc: Document | DropBoxDocument,
    validateMode: boolean
  ): Observable<boolean> {
    const read = new Subject<boolean>();
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PdfViewerDialogComponent, {
        data: { doc, validateMode },
      });
      dialogRef.afterClosed().subscribe((result) => read.next(result));
    }
    return read.asObservable();
  }

  public showHelpScoutArticle(articleId: string) {
    if (this.appConfigService.isMobileOrTablet) {
      // on mobile, route to the help page with show article queryParam
      const queryParams = {};
      queryParams[AppConstants.PARAM_BEACON_ARTICLE_ID] = articleId;
      this.router.navigate(["help"], { queryParams });
    } else {
      // on desktop, show article directly
      this.showBeaconArticle(articleId);
    }
  }

  public showBeaconArticle(articleId: string) {
    const Beacon = window["Beacon"];
    Beacon("article", articleId);
  }
}
